import { supabase } from "../supabase/client";
import type { Database } from "../database.types";
import { v4 as uuidv4 } from "uuid";

type Profile = Database["public"]["Tables"]["profiles"]["Insert"];
type Subscription = Database["public"]["Tables"]["subscriptions"]["Insert"];

interface SignUpData {
  email: string;
  password: string;
  full_name: string;
  plan_id?: string;
  is_trial?: boolean;
  phone_number?: string;
  cedula?: string;
  tenant_name?: string;
  tenant_id?: string;
  referral_code?: string;
}

export async function signUpWithTransaction({
  email,
  password,
  full_name,
  plan_id, // Default plan
  is_trial = true,
  phone_number,
  cedula,
  tenant_name,
  tenant_id,
  referral_code,
}: SignUpData) {
  try {
    // 1. First check if user exists
    const { data: existingUser, error: checkError } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", email)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existingUser) {
      throw new Error("Este correo electrónico ya está registrado");
    }

    // Check for duplicate cedula if provided
    if (cedula) {
      const { data: existingCedula, error: cedulaError } = await supabase
        .from("profiles")
        .select("id")
        .eq("cedula", cedula)
        .maybeSingle();

      if (cedulaError) throw cedulaError;
      if (existingCedula) {
        throw new Error("Esta cédula ya está registrada");
      }
    }

    // 2. Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      phone: phone_number,
      options: {
        data: {
          full_name,
          cedula,
          phone: phone_number,
          referral_code: referral_code ? referral_code.trim().toUpperCase() : undefined,
        },
      },
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error("No user data returned");

    const userId = authData.user.id;
    // User ID retrieved for auth component

    // 3. Create profile and subscription in a transaction
    const { error: transactionError } = await supabase.rpc("handle_new_user", {
      p_user_id: userId,
      p_email: email,
      p_full_name: full_name,
      p_plan_id: plan_id,
      p_is_trial: is_trial,
      p_trial_days: 3,
      p_cedula: cedula,
      p_phone: phone_number,
      p_tenant_name: tenant_name ?? null,
      p_tenant_slug: tenant_name ? tenant_name.toLowerCase().replace(/[^a-z0-9]+/g, "-") : null,
      p_tenant_id: tenant_id ?? null,
    });


    // 4. Si se proporciono un codigo de referido, registrar en public.referrals
    if (referral_code && referral_code.trim() !== "") {
      try {
        await (supabase as any).rpc("register_referral_by_code", {
          p_referred_id: userId,
          p_referral_code: referral_code.trim(),
          p_referred_email: email,
        });
      } catch (refErr) {
        console.warn("No se pudo vincular referido:", refErr);
      }
    }

    if (transactionError) {
      console.error("Transaction error:", transactionError);

      // Handle specific database constraint errors
      if (transactionError.code === "23503") {
        // sForeign key constraint violation
        if (transactionError.message.includes("subscriptions_user_id_fkey")) {
          throw new Error(
            "Error interno del sistema. Por favor, inténtalo de nuevo."
          );
        }
        throw new Error(
          "Error de validación de datos. Verifica que todos los campos sean correctos."
        );
      } else if (transactionError.code === "23505") {
        // Unique constraint violation
        if (transactionError.message.includes("profiles_email_key")) {
          throw new Error("Este correo electrónico ya está registrado");
        } else if (transactionError.message.includes("profiles_cedula_key")) {
          throw new Error("Esta cédula ya está registrada");
        } else if (transactionError.message.includes("profiles_pkey")) {
          // Generate a new UUID and try again
          const newUserId = uuidv4();
          const { error: retryError } = await supabase.rpc("handle_new_user", {
            p_user_id: newUserId,
            p_email: email,
            p_full_name: full_name,
            p_plan_id: plan_id,
            p_is_trial: is_trial,
            p_trial_days: 3,
            p_cedula: cedula,
            p_phone: phone_number,
            p_tenant_name: tenant_name ?? null,
            p_tenant_slug: tenant_name ? tenant_name.toLowerCase().replace(/[^a-z0-9]+/g, "-") : null,
            p_tenant_id: tenant_id ?? null,
          });

          if (retryError) {
            throw new Error(
              "Error interno del sistema. Por favor, inténtalo de nuevo."
            );
          }
        } else if (transactionError.message.includes("unique_user_id")) {
          throw new Error(
            "Ya tienes una invitación activa. No puedes crear múltiples invitaciones simultáneamente."
          );
        } else {
          throw new Error("Los datos proporcionados ya existen en el sistema.");
        }
      } else if (transactionError.code === "409") {
        throw new Error(
          "Conflicto de datos. Este correo electrónico ya está registrado."
        );
      } else {
        // Generic error handling
        throw new Error(
          "Error interno del sistema. Por favor, inténtalo de nuevo."
        );
      }
    }

    return { user: authData.user, error: null };
  } catch (error) {
    console.error("Error in signUpWithTransaction:", error);
    return {
      user: null,
      error:
        error instanceof Error
          ? error.message
          : "An error occurred during signup",
    };
  }
}

export const verifyEmail = async (token: string, email: string) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email",
    });

    if (error) {
      return { error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    return { error: "Error al verificar el email" };
  }
};

export const resendConfirmationEmail = async (email: string) => {
  try {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (error) {
    return { error: "Error al reenviar el correo de confirmación" };
  }
};
